// AdminUsers — players list, redesigned as a summary card grid.
//
// Design:
//   • Sticky header with search + status filter pills (All / Active /
//     Suspended / Banned / Pending). Pending count chips in each pill.
//   • Grid of player cards. Each card has avatar, display name, @handle,
//     status pill, wallet balance, contact line, joined date. Whole
//     card is the tap-target into AdminUserDetail.
//   • Mobile: 1 col. Tablet: 2 col. Desktop: 3 col. Wide: 4 col.
//   • No tables — they don't work on phones and the card carries the
//     same info more compactly.
//
// Light + dark designed.

import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'motion/react';
import AdminAvatar from '@baaligames/shared-ui/admin/components/AdminAvatar';
import Icon from '@baaligames/shared-ui/components/Icon';
import PhoneContact from '@baaligames/shared-ui/components/PhoneContact';
import { adminApi } from '@baaligames/shared-ui/admin/lib/adminApi';
import { useAdminAuth } from '@baaligames/shared-ui/admin/lib/adminAuth';
import { ApiError } from '@baaligames/shared-ui/lib/api';
import { useT } from '@baaligames/shared-ui/lib/i18n';
import CreatePlayerModal from '@baaligames/shared-ui/admin/components/CreatePlayerModal';
import ShareableIdCard from '../../components/ShareableIdCard';
import AcceptingPlayersToggleCard from './AcceptingPlayersToggleCard';

type UserRow = {
  id: number;
  username: string;
  email: string;
  phone: string | null;
  first_name: string | null;
  last_name: string | null;
  profile_image_url: string | null;
  status: 'pending' | 'active' | 'suspended' | 'banned';
  email_verified_at: string | null;
  telegram_verified_at: string | null;
  failed_login_attempts: number;
  locked_until: string | null;
  created_at: string;
  last_login_at: string | null;
  wallet_balance: number;
};

type ListResp = { rows: UserRow[]; total: number; limit: number; offset: number };

const PAGE_SIZE = 24;

// Status filter pill labels — translated at render time via t() to keep
// language switches reactive. Keys are stable; only `labelKey` is read.
const STATUS_OPTS: Array<{ key: '' | UserRow['status']; labelKey: string; tone: string }> = [
  { key: '',          labelKey: 'agent.playersList.filter.all',       tone: 'neutral' },
  { key: 'active',    labelKey: 'agent.playersList.filter.active',    tone: 'ok' },
  { key: 'pending',   labelKey: 'agent.playersList.filter.pending',   tone: 'warn' },
  { key: 'suspended', labelKey: 'agent.playersList.filter.suspended', tone: 'warn' },
  { key: 'banned',    labelKey: 'agent.playersList.filter.banned',    tone: 'danger' },
];

const STATUS_PILL: Record<UserRow['status'], string> = {
  active:    'bg-emerald-50 text-emerald-700 border border-emerald-200 dark:bg-emerald-400/10 dark:text-emerald-300 dark:border-emerald-400/30',
  pending:   'bg-amber-50 text-amber-700 border border-amber-200 dark:bg-amber-400/10 dark:text-amber-300 dark:border-amber-400/30',
  suspended: 'bg-amber-100 text-amber-800 border border-amber-300 dark:bg-amber-400/15 dark:text-amber-200 dark:border-amber-400/40',
  banned:    'bg-rose-50 text-rose-700 border border-rose-200 dark:bg-rose-400/10 dark:text-rose-300 dark:border-rose-400/30',
};

const inr = (n: number) => `₹${(n || 0).toLocaleString('en-IN')}`;

export default function PlayersListPanel() {
  const t = useT();
  const { admin, canDo } = useAdminAuth();
  const canAddPlayer = canDo('users_create', 'edit');
  const [data, setData] = useState<ListResp | null>(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [debounced, setDebounced] = useState('');
  const [statusFilter, setStatusFilter] = useState<'' | UserRow['status']>('');
  const [page, setPage] = useState(0);
  const [creating, setCreating] = useState(false);

  // Debounce search 250ms.
  useEffect(() => {
    const t = setTimeout(() => setDebounced(search.trim()), 250);
    return () => clearTimeout(t);
  }, [search]);

  const load = useCallback(async () => {
    setLoading(true); setErr(null);
    try {
      const q = new URLSearchParams();
      q.set('limit', String(PAGE_SIZE));
      q.set('offset', String(page * PAGE_SIZE));
      if (debounced) q.set('search', debounced);
      if (statusFilter) q.set('status', statusFilter);
      const d = await adminApi.get<ListResp>(`/admin/users?${q.toString()}`);
      setData(d);
    } catch (e) { setErr((e as ApiError).message || t('agent.activity.error.loadFailed')); }
    finally { setLoading(false); }
  }, [debounced, statusFilter, page, t]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { setPage(0); }, [debounced, statusFilter]);

  const totalPages = data ? Math.max(1, Math.ceil(data.total / PAGE_SIZE)) : 1;
  const showingFrom = data ? data.offset + 1 : 0;
  const showingTo   = data ? Math.min(data.offset + data.rows.length, data.total) : 0;

  const headerSubtitle = useMemo(() => {
    if (loading && !data) return t('agent.playersList.loading');
    if (!data) return '';
    return data.total === 0
      ? t('agent.playersList.empty')
      : t('agent.playersList.results', { from: String(showingFrom), to: String(showingTo), total: data.total.toLocaleString('en-IN') });
  }, [data, loading, showingFrom, showingTo, t]);

  return (
    <div className="space-y-4">
      {/* Phase 6.3 (2026-05-11): the "Receiving new players" locator
          toggle lives here, directly above the Shareable Agent ID
          card. It controls the same surface — who lands on the
          agent's player roster — so it belongs next to the player
          list rather than buried in the Dashboard accordion. Uses
          the ORIGINAL design from the old /me/dashboard (border-2,
          solid pill background, full descriptive copy). */}
      <AcceptingPlayersToggleCard />

      {/* Shareable Agent ID — Phase 4 follow-up (2026-05-11): moved
          from AccountPanel into this Players list sub-tab, sitting
          ABOVE the Add Player toolbar. The UAID is the handle agents
          share so another agent can transfer players to them, so it
          belongs next to the people surface. Only rendered if the
          backend has minted a UAID for this agent. */}
      {admin?.uaid && <ShareableIdCard uaid={admin.uaid} />}

      {/* Phase 3.5: this panel now lives inside PlayersHub which owns
          the title + icon row. Render only the count + Add button as
          a compact toolbar to avoid a duplicate header. */}
      <motion.div initial={{ opacity: 0, y: 4 }} animate={{ opacity: 1, y: 0 }}
                  className="flex items-center justify-between gap-2">
        <p className="text-[12px] text-arena-600 dark:text-neon-200/80 truncate">{headerSubtitle}</p>
        {canAddPlayer && (
          <button
            onClick={() => setCreating(true)}
            className="shrink-0 inline-flex items-center gap-1 rounded-full
                       bg-emerald-600 hover:bg-emerald-500 active:bg-emerald-700
                       text-white text-[12px] font-bold px-3 py-1.5
                       shadow-[0_2px_10px_rgba(16,185,129,0.35)] transition active:scale-95"
          >
            <Icon name="person_add" size={14} /> {t('agent.playersList.add')}
          </button>
        )}
      </motion.div>

      {/* Search + filters */}
      <div className="flex flex-col sm:flex-row gap-2 sm:items-center">
        <div className="relative flex-1 min-w-0">
          <Icon name="search" size={16}
                className="absolute left-3 top-1/2 -translate-y-1/2 text-arena-600 dark:text-neon-200/80 pointer-events-none" />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={t('agent.playersList.searchPlaceholder')}
            className="w-full rounded-full pl-9 pr-3 py-2 text-[13px]
                       bg-white dark:bg-arena-700/40
                       border border-arena-200/60 dark:border-arena-600/50
                       text-arena-900 dark:text-white
                       placeholder-neutral-400 dark:placeholder-white/40
                       focus:outline-none focus:ring-2 focus:ring-violet-400/40 focus:border-violet-400/40"
          />
          {search && (
            <button onClick={() => setSearch('')} className="absolute right-2 top-1/2 -translate-y-1/2
                                                              p-1 rounded-full hover:bg-arena-100/60 dark:hover:bg-arena-600/55">
              <Icon name="close" size={14} className="text-arena-600 dark:text-neon-200/80" />
            </button>
          )}
        </div>
        <div className="flex gap-1.5 overflow-x-auto no-scrollbar">
          {STATUS_OPTS.map((o) => {
            const active = o.key === statusFilter;
            return (
              <button
                key={o.key || 'all'}
                onClick={() => setStatusFilter(o.key)}
                className={`shrink-0 px-3.5 py-1.5 rounded-full text-[12px] font-semibold transition border
                  ${active
                    ? 'bg-violet-600 text-white border-violet-600 shadow-[0_2px_10px_rgba(139,92,246,0.35)] dark:bg-white dark:text-arena-900 dark:border-white'
                    : 'bg-white text-arena-700 border-arena-200/60 hover:bg-arena-50/40 dark:bg-arena-700/40 dark:text-neon-200/80 dark:border-arena-600/50 dark:hover:bg-arena-600/55'}`}
              >
                {t(o.labelKey)}
              </button>
            );
          })}
        </div>
      </div>

      {err && (
        <div className="rounded-2xl bg-rose-50 dark:bg-rose-500/10 border border-rose-200 dark:border-rose-400/30 text-rose-800 dark:text-rose-200 px-4 py-3 text-sm">
          {err}
        </div>
      )}

      {/* Skeleton or grid — single column in the hub's narrow column */}
      {loading && !data && (
        <div className="grid grid-cols-1 gap-3">
          {Array.from({ length: 4 }).map((_, i) => (
            <div key={i} className="rounded-2xl bg-white dark:bg-arena-700/40 border border-arena-200/60 dark:border-arena-600/50 p-4 animate-pulse">
              <div className="h-12 mb-3 bg-arena-100/60 dark:bg-arena-700/40 rounded-xl" />
              <div className="h-4 mb-2 bg-arena-100/60 dark:bg-arena-700/40 rounded" />
              <div className="h-3 mb-2 bg-arena-100/60 dark:bg-arena-700/40 rounded w-2/3" />
            </div>
          ))}
        </div>
      )}

      {data && data.rows.length === 0 && !loading && (
        <div className="rounded-2xl bg-white dark:bg-arena-700/40 border border-arena-200/60 dark:border-arena-600/50 p-10 text-center">
          <Icon name="person_search" size={32} className="text-arena-500 dark:text-neon-200/60 mx-auto mb-2" />
          <p className="text-[13px] text-arena-700 dark:text-neon-200/80 font-semibold">{t('agent.playersList.empty.title')}</p>
          <p className="text-[11.5px] text-arena-600 dark:text-neon-200/70 mt-0.5">
            {t('agent.playersList.empty.body')}
          </p>
        </div>
      )}

      {data && data.rows.length > 0 && (
        <div className="grid grid-cols-1 gap-3">
          {data.rows.map((u, i) => (
            <PlayerCard key={u.id} u={u} index={i} />
          ))}
        </div>
      )}

      {/* Pagination */}
      {data && data.total > PAGE_SIZE && (
        <div className="flex items-center justify-end gap-2 pt-1 text-[12px] text-arena-600 dark:text-neon-200/80">
          <button disabled={page === 0}
            onClick={() => setPage((p) => Math.max(0, p - 1))}
            className="grid place-items-center w-9 h-9 rounded-full
                       bg-white dark:bg-arena-700/40 border border-arena-200/60 dark:border-arena-600/50
                       disabled:opacity-40 hover:bg-arena-50/40 dark:hover:bg-arena-600/55">
            <Icon name="chevron_left" size={16} />
          </button>
          <span className="tabular-nums">{t('agent.playersList.pagination', { page: String(page + 1), totalPages: String(totalPages) })}</span>
          <button disabled={page >= totalPages - 1}
            onClick={() => setPage((p) => p + 1)}
            className="grid place-items-center w-9 h-9 rounded-full
                       bg-white dark:bg-arena-700/40 border border-arena-200/60 dark:border-arena-600/50
                       disabled:opacity-40 hover:bg-arena-50/40 dark:hover:bg-arena-600/55">
            <Icon name="chevron_right" size={16} />
          </button>
        </div>
      )}

      {creating && (
        <CreatePlayerModal
          mode="agent"
          callerAgentId={admin?.id}
          onClose={() => setCreating(false)}
          onCreated={() => { setCreating(false); load(); }}
        />
      )}
    </div>
  );
}

// ─── Player card ───────────────────────────────────────────────────

function PlayerCard({ u, index }: { u: UserRow; index: number }) {
  const t = useT();
  const displayName = (u.first_name || u.last_name)
    ? `${u.first_name || ''} ${u.last_name || ''}`.trim()
    : u.username;
  const verified = !!u.email_verified_at || !!u.telegram_verified_at;
  const statusLabel = t(`agent.playersList.card.status.${u.status}`);
  return (
    <motion.div
      initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.25, delay: Math.min(index * 0.02, 0.2) }}
    >
      <Link
        to={`/people/players/${u.id}`}
        className="block rounded-2xl p-4
                   bg-white dark:bg-arena-700/40
                   border border-arena-200/60 dark:border-arena-600/50 ring-1 ring-cyan-200/50 dark:ring-cyan-400/15
                   shadow-[0_1px_3px_rgba(0,0,0,0.04)] dark:shadow-none
                   hover:-translate-y-0.5 hover:ring-cyan-300 dark:hover:ring-cyan-400/40 transition
                   focus:outline-none focus-visible:ring-2 focus-visible:ring-violet-400/50"
      >
        <div className="flex items-start gap-3 mb-3">
          <AdminAvatar url={u.profile_image_url} username={u.username} size="md" />
          <div className="min-w-0 flex-1">
            <div className="text-[14px] font-semibold text-arena-900 dark:text-white truncate">
              {displayName}
            </div>
            <div className="text-[11px] text-arena-600 dark:text-neon-200/80 truncate">
              @{u.username} · #{u.id}
            </div>
            <div className="flex items-center gap-1.5 mt-1.5 flex-wrap">
              <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10.5px] font-semibold capitalize ${STATUS_PILL[u.status]}`}>
                <span className="w-1.5 h-1.5 rounded-full bg-current" />
                {statusLabel}
              </span>
              {verified && (
                <span className="inline-flex items-center gap-0.5 rounded-full px-1.5 py-0.5 text-[10px] font-semibold
                                 bg-cyan-50 text-cyan-700 border border-cyan-200
                                 dark:bg-cyan-400/10 dark:text-cyan-300 dark:border-cyan-400/30">
                  <Icon name="verified" size={11} filled /> {t('agent.playersList.card.verifiedBadge')}
                </span>
              )}
            </div>
          </div>
          <Icon name="chevron_right" size={16} className="text-arena-500 dark:text-neon-200/60 shrink-0" />
        </div>

        {/* Wallet + contact */}
        <div className="grid grid-cols-2 gap-2 border-t border-arena-200/40 dark:border-arena-600/40 pt-3">
          <div className="min-w-0">
            <div className="text-[10.5px] text-arena-600 dark:text-neon-200/70 truncate">{t('agent.playersList.column.wallet')}</div>
            <div className="text-[14px] font-display font-extrabold text-cyan-700 dark:text-cyan-300 tabular-nums truncate">
              {inr(u.wallet_balance)}
            </div>
          </div>
          <div className="min-w-0">
            <div className="text-[10.5px] text-arena-600 dark:text-neon-200/70 truncate">{t('agent.playersList.column.joined')}</div>
            <div className="text-[12px] font-semibold text-arena-900 dark:text-white truncate">
              {new Date(u.created_at).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: '2-digit' })}
            </div>
            <div className="text-[9.5px] text-arena-600 dark:text-neon-200/70 truncate mt-1" title={u.last_login_at || ''}>
              {t('agent.subAgentDetail.identity.row.lastLogin')}: {u.last_login_at ? new Date(u.last_login_at).toLocaleString('en-IN', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' }) : '—'}
            </div>
          </div>
        </div>

        {(u.email || u.phone) && (
          <div className="mt-2.5 text-[10.5px] text-arena-600 dark:text-neon-200/70 truncate flex items-center gap-1">
            {u.phone ? <PhoneContact phone={u.phone} name={u.username} iconSize={10} className="font-mono" /> : null}
            {u.phone && u.email ? <span className="mx-0.5">·</span> : null}
            {u.email ? <span className="truncate">{u.email}</span> : null}
          </div>
        )}
      </Link>
    </motion.div>
  );
}
